import React, { useState } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import GiftForm from '../GiftForm';
import AlertDialog from '../AlertDialog';

interface GiftPurchase {
  purchaser_name: string;
  phone: string;
  message: string | null;
  purchased_at: string;
}

interface Gift {
  id: string;
  name: string;
  description: string;
  image_url: string;
  price: number;
  pix_key: string;
  purchase_count: number;
  purchases: GiftPurchase[];
}

interface GiftManagementProps {
  gifts: Gift[];
  onAddGift: (data: any) => Promise<void>;
  onEditGift: (data: any) => Promise<void>;
  onDeleteGift: (id: string) => Promise<void>;
}

const GiftManagement: React.FC<GiftManagementProps> = ({
  gifts,
  onAddGift,
  onEditGift,
  onDeleteGift
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedGift, setSelectedGift] = useState<Gift | null>(null);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  const handleSubmit = async (data: any) => {
    try {
      if (selectedGift) {
        await onEditGift({ ...data, id: selectedGift.id });
        setAlertMessage('Presente atualizado com sucesso!');
      } else {
        await onAddGift(data);
        setAlertMessage('Presente adicionado com sucesso!');
      }
      setShowModal(false);
      setSelectedGift(null);
      setShowAlert(true);
    } catch (error) {
      console.error('Error handling gift:', error);
      setAlertMessage('Erro ao salvar presente. Por favor, tente novamente.');
      setShowAlert(true);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await onDeleteGift(id);
      setAlertMessage('Presente excluído com sucesso!');
      setShowAlert(true);
    } catch (error) {
      console.error('Error deleting gift:', error);
      setAlertMessage('Erro ao excluir presente. Por favor, tente novamente.');
      setShowAlert(true);
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-4 bg-wedding-beige-100 border-b flex justify-between items-center">
          <h3 className="font-medium text-wedding-blue-800">Lista de Presentes</h3>
          <button
            onClick={() => {
              setSelectedGift(null);
              setShowModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-wedding-blue-600 text-white rounded-md hover:bg-wedding-blue-700 transition-colors"
          >
            <Plus size={18} />
            Adicionar Presente
          </button>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Presente
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Imagem
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Descrição
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valor
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Chave Pix
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Comprados
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {gifts.map((gift) => (
                <tr key={gift.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{gift.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="w-16 h-16 bg-gray-50">
                      <img 
                        src={gift.image_url} 
                        alt={gift.name}
                        className="w-full h-full object-contain rounded-md"
                      />
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500 max-w-xs">{gift.description}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">R$ {gift.price.toFixed(2)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{gift.pix_key}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{gift.purchase_count}x</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setSelectedGift(gift);
                          setShowModal(true);
                        }}
                        className="p-1 text-wedding-blue-600 hover:bg-wedding-blue-50 rounded-full transition-colors"
                        title="Editar presente"
                      >
                        <Pencil size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(gift.id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                        title="Excluir presente"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <div className="p-4 border-t">
          <p className="text-sm text-gray-500">
            Total de presentes cadastrados: <span className="font-medium">{gifts.length}</span>
          </p>
          <p className="text-sm text-gray-500">
            Total de presentes comprados: <span className="font-medium">
              {gifts.reduce((acc, gift) => acc + gift.purchase_count, 0)}
            </span>
          </p>
          <p className="text-sm text-gray-500">
            Valor total em presentes: <span className="font-medium">
              R$ {gifts.reduce((acc, gift) => acc + (gift.price * gift.purchase_count), 0).toFixed(2)}
            </span>
          </p>
        </div>
      </div>

      {showModal && (
        <GiftForm
          onClose={() => {
            setShowModal(false);
            setSelectedGift(null);
          }}
          onSubmit={handleSubmit}
          initialData={selectedGift ? {
            id: selectedGift.id,
            name: selectedGift.name,
            description: selectedGift.description,
            price: selectedGift.price,
            pixKey: selectedGift.pix_key,
            imageUrl: selectedGift.image_url
          } : undefined}
          mode={selectedGift ? 'edit' : 'create'}
        />
      )}

      <AlertDialog
        isOpen={showAlert}
        title="Notificação"
        message={alertMessage}
        onClose={() => setShowAlert(false)}
      />
    </>
  );
};

export default GiftManagement;